import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { 
  User as FirebaseUser, 
  signOut, 
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, IS_MOCK_MODE } from '@/lib/firebase';
import { accountApi, userApi, UserResponseDto } from '@/lib/api';

// Generate UUID function
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

interface UserProfile {
  uid: string;
  accountId: string; // UUID for account identification
  phoneNumber: string;
  fullName?: string;
  email?: string;
  profileImage?: string;
  joinDate: Date;
  termsAccepted?: boolean;
  termsAcceptedDate?: Date;
  profileComplete: boolean;
  backendAccount?: UserResponseDto; // Add backend user data
  networkError?: boolean; // Flag to indicate network connectivity issues
}

// Helper function to detect network errors
function isNetworkError(error: any): boolean {
  // Check for common network error indicators
  return (
    error.isNetworkError ||
    error instanceof TypeError && error.message.includes('fetch') ||
    error.name === 'NetworkError' ||
    error.code === 'NETWORK_ERROR' ||
    error.message?.includes('Failed to fetch') ||
    error.message?.includes('Network request failed') ||
    !navigator.onLine
  );
}

interface UserContextType {
  user: UserProfile | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  setupRecaptcha: (elementId: string) => RecaptchaVerifier | null;
  sendOTP: (phoneNumber: string) => Promise<ConfirmationResult | any>;
  verifyOTP: (confirmationResult: ConfirmationResult | any, code: string) => Promise<void>;
  completeProfile: (profileData: { fullName: string; email: string }) => Promise<void>;
  updateProfile: (profileData: { fullName: string; email: string; profileImage?: string | null }) => Promise<void>;
  acceptTerms: () => Promise<void>;
  syncBackendAccount: () => Promise<UserResponseDto | null>;
  logout: () => Promise<void>;
  resetPassword: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [otpVerified, setOtpVerified] = useState(false);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null); // Use ref to avoid re-renders

  useEffect(() => {
    // Check if Firebase is configured
    if (!auth) {
      console.warn('⚠️ Firebase auth not initialized. Skipping auth state monitoring.');
      setLoading(false);
      return;
    }
    let unsubscribe: (() => void) | undefined;

    if (IS_MOCK_MODE) {
      // Handle mock authentication
      unsubscribe = (auth as any).onAuthStateChanged(async (mockUser: any) => {
        setFirebaseUser(mockUser);
        
        if (mockUser) {
          // Handle mock user profile
          const userProfileKey = `firestore-users/${mockUser.uid}`;
          const stored = localStorage.getItem(userProfileKey);
          
          if (stored) {
            const existingProfile = JSON.parse(stored) as UserProfile;
            
            // Ensure backwards compatibility for existing mock users
            const updatedProfile = {
              ...existingProfile,
              termsAccepted: existingProfile.termsAccepted ?? true, // Assume old users accepted terms
              joinDate: existingProfile.joinDate || new Date()
            };
            
            setUser(updatedProfile);
          } else {
            // Create basic user profile for new mock user            
            const basicProfile: UserProfile = {
              uid: mockUser.uid,
              accountId: generateUUID(),
              phoneNumber: mockUser.phoneNumber || '',
              joinDate: new Date(),
              termsAccepted: false, // New users must accept terms first
              profileComplete: false // Then complete profile
            };
            
            localStorage.setItem(userProfileKey, JSON.stringify(basicProfile));
            setUser(basicProfile);
          }
        } else {
          setUser(null);
        }
        
        setLoading(false);
      });
    } else {
      // Handle real Firebase authentication
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        setFirebaseUser(firebaseUser);
        
        if (firebaseUser) {
          // Determine if we should fetch user data from backend
          const shouldFetchUserData = otpVerified || !user || !user.backendAccount; // Either just verified OTP, first load, or missing backend data
          
          if (shouldFetchUserData) {
            // Fetch user data from backend to determine profile state
            try {
              const backendUser = await userApi.getCurrentUser();
              // Create profile from backend data
              const userProfile: UserProfile = {
                uid: firebaseUser.uid,
                accountId: backendUser.accountId,
                phoneNumber: firebaseUser.phoneNumber || '',
                fullName: backendUser.fullName,
                email: backendUser.email,
                profileImage: backendUser.profileImage,
                joinDate: new Date(),
                termsAccepted: backendUser.termsAndConditionsAccepted,
                termsAcceptedDate: backendUser.termsAcceptedAt ? new Date(backendUser.termsAcceptedAt) : undefined,
                profileComplete: !!(backendUser.fullName && backendUser.termsAndConditionsAccepted),
                backendAccount: backendUser
              };
              setUser(userProfile);
              
              // Reset the OTP verified flag
              setOtpVerified(false);
            } catch (error: any) {
              if (error.status === 404) {
                // If user doesn't exist, trigger 401 page
                throw { ...error, isAuthError: true };
              }
              // Check if this is a network error
              if (isNetworkError(error)) {
                const networkErrorProfile: UserProfile = {
                  uid: firebaseUser.uid,
                  accountId: generateUUID(),
                  phoneNumber: firebaseUser.phoneNumber || '',
                  joinDate: new Date(),
                  termsAccepted: true, // Assume terms were accepted to avoid showing terms dialog
                  profileComplete: false,
                  networkError: true // Flag to indicate network issues
                };
                setUser(networkErrorProfile);
              } else {
                // Create minimal fallback profile on other errors
                const fallbackProfile: UserProfile = {
                  uid: firebaseUser.uid,
                  accountId: generateUUID(),
                  phoneNumber: firebaseUser.phoneNumber || '',
                  joinDate: new Date(),
                  termsAccepted: false,
                  profileComplete: false
                };
                setUser(fallbackProfile);
              }
              
              // Reset the OTP verified flag
              setOtpVerified(false);
            }
          } else {
            console.log('⏸️ Skipping backend fetch - user already loaded and OTP not just verified');
          }
        } else {
          setUser(null);
          setOtpVerified(false); // Reset flag when user logs out
        }
        
        setLoading(false);
      });
    }

    return () => {
      unsubscribe?.();
      // Clean up reCAPTCHA verifier on unmount
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (error) {
          console.warn('Failed to clear reCAPTCHA on unmount:', error);
        }
      }
    };
  }, []);

  const setupRecaptcha = (elementId: string): RecaptchaVerifier | null => {
    if (IS_MOCK_MODE) {
      return null; // Mock doesn't need reCAPTCHA
    }
    
    if (!auth) {
      throw new Error('Firebase auth not configured');
    }

    // If we already have a verifier, clear it first
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
      } catch (error) {
        console.warn('Failed to clear existing reCAPTCHA:', error);
      }
      recaptchaVerifierRef.current = null;
    }

    // Clear the container element
    const container = document.getElementById(elementId);
    if (container) {
      container.innerHTML = '';
    }

    try {
      const newVerifier = new RecaptchaVerifier(auth, elementId, {
        size: 'invisible',
        callback: () => {
          console.log('✅ reCAPTCHA solved successfully');
        },
        'expired-callback': () => {
          console.warn('⚠️ reCAPTCHA expired');
        }
      });
      
      recaptchaVerifierRef.current = newVerifier;
      return newVerifier;
    } catch (error) {
      console.error('❌ Failed to setup reCAPTCHA:', error);
      throw new Error('reCAPTCHA setup failed. Please refresh the page and try again.');
    }
  };

  const sendOTP = async (phoneNumber: string): Promise<ConfirmationResult | any> => {
    if (!auth) {
      throw new Error('Auth not configured. Please set up your authentication.');
    }

    if (IS_MOCK_MODE) {
      // Use mock authentication
      return await (auth as any).signInWithPhoneNumber(phoneNumber);
    } else {
      // Use real Firebase authentication
      try {
        const verifier = setupRecaptcha('recaptcha-container');
        if (!verifier) {
          throw new Error('reCAPTCHA setup failed');
        }
        
        console.log('📱 Sending OTP to:', phoneNumber);
        return await signInWithPhoneNumber(auth, phoneNumber, verifier);
      } catch (error: any) {
        console.error('❌ Failed to send OTP:', error);
        
        // Provide user-friendly error messages
        if (error.code === 'auth/captcha-check-failed') {
          throw new Error('reCAPTCHA verification failed. Please ensure your domain is configured in Firebase Console.');
        } else if (error.code === 'auth/invalid-phone-number') {
          throw new Error('Invalid phone number format. Please check and try again.');
        } else if (error.code === 'auth/too-many-requests') {
          throw new Error('Too many requests. Please wait a moment before trying again.');
        } else {
          throw new Error(error.message || 'Failed to send OTP. Please try again.');
        }
      }
    }
  };

  const verifyOTP = async (confirmationResult: ConfirmationResult | any, code: string): Promise<void> => {
    await confirmationResult.confirm(code);
    // Mark that OTP was successfully verified
    setOtpVerified(true);
    // syncBackendAccount will be called automatically by the onAuthStateChanged listener
    // But also force a sync to ensure data is loaded
    setTimeout(() => {
      syncBackendAccount();
    }, 1000); // Small delay to ensure auth state is updated
  };

  const syncBackendAccount = async (): Promise<UserResponseDto | null> => {
    if (!auth?.currentUser || IS_MOCK_MODE) {
      return null;
    }

    try {
      console.log('🔄 Refreshing user data from backend...');
      const backendUser = await userApi.getCurrentUser();
      console.log('✅ Refreshed user data:', backendUser);
      
      // Update existing user profile with latest backend data
      if (user) {
        const updatedProfile: UserProfile = {
          ...user,
          backendAccount: backendUser,
          accountId: backendUser.accountId,
          fullName: backendUser.fullName,
          email: backendUser.email,
          profileImage: backendUser.profileImage,
          termsAccepted: backendUser.termsAndConditionsAccepted,
          termsAcceptedDate: backendUser.termsAcceptedAt ? new Date(backendUser.termsAcceptedAt) : user.termsAcceptedDate,
          profileComplete: !!(backendUser.fullName && backendUser.termsAndConditionsAccepted)
        };
        setUser(updatedProfile);
      }
      
      return backendUser;
    } catch (error: any) {
      console.error('❌ Failed to refresh user data:', error);
      throw error;
    }
  };

  const completeProfile = async (profileData: { fullName: string; email: string }): Promise<void> => {
    if (!firebaseUser) throw new Error('No authenticated user');
    
    const updatedProfile: UserProfile = {
      uid: firebaseUser.uid,
      accountId: user?.accountId || generateUUID(),
      phoneNumber: firebaseUser.phoneNumber || '',
      fullName: profileData.fullName,
      email: profileData.email,
      joinDate: user?.joinDate || new Date(),
      termsAccepted: user?.termsAccepted || false, // Preserve terms acceptance state
      termsAcceptedDate: user?.termsAcceptedDate,
      profileComplete: true,
      backendAccount: user?.backendAccount // Preserve backend account info
    };
    
    if (IS_MOCK_MODE) {
      // Save to localStorage for mock mode
      const userProfileKey = `firestore-users/${firebaseUser.uid}`;
      localStorage.setItem(userProfileKey, JSON.stringify(updatedProfile));
    } else {
      try {
        // Update profile via backend API
        console.log('👤 Updating profile via backend API...');
        const updatedBackendUser = await userApi.updateCurrentUser({
          fullName: profileData.fullName,
          email: profileData.email
        });
        console.log('✅ Profile updated via backend:', updatedBackendUser);
        
        // Update local profile with backend response
        const finalProfile: UserProfile = {
          ...updatedProfile,
          backendAccount: updatedBackendUser,
          // Ensure data consistency with backend
          accountId: updatedBackendUser.accountId,
          fullName: updatedBackendUser.fullName,
          email: updatedBackendUser.email,
          termsAccepted: updatedBackendUser.termsAndConditionsAccepted,
          termsAcceptedDate: updatedBackendUser.termsAcceptedAt ? new Date(updatedBackendUser.termsAcceptedAt) : updatedProfile.termsAcceptedDate
        };
        
        setUser(finalProfile);
        return;
      } catch (error) {
        console.error('❌ Failed to update profile via backend:', error);
        throw error; // Don't continue with fallback, just throw the error
      }
    }
    
    setUser(updatedProfile);
  };

  const updateProfile = async (profileData: { fullName: string; email: string; profileImage?: string | null }): Promise<void> => {
    if (!firebaseUser) throw new Error('No authenticated user');
    if (!user) throw new Error('No user profile found');
    
    // Update profile data via backend API
    try {
      console.log('👤 Updating profile via backend API...');
      const updatedBackendUser = await userApi.updateCurrentUser({
        fullName: profileData.fullName,
        email: profileData.email,
        profileImage: profileData.profileImage !== undefined ? profileData.profileImage || undefined : undefined
      });
      console.log('✅ Profile updated via backend:', updatedBackendUser);
      
      const updatedProfile: UserProfile = {
        ...user,
        backendAccount: updatedBackendUser,
        fullName: updatedBackendUser.fullName,
        email: updatedBackendUser.email,
        profileImage: updatedBackendUser.profileImage || user.profileImage,
        termsAccepted: updatedBackendUser.termsAndConditionsAccepted,
        profileComplete: !!(updatedBackendUser.fullName && updatedBackendUser.termsAndConditionsAccepted)
      };
      
      setUser(updatedProfile);
    } catch (error) {
      console.error('❌ Failed to update profile via backend:', error);
      throw error;
    }
  };

  const acceptTerms = async (): Promise<void> => {
    if (!firebaseUser) throw new Error('No authenticated user');
    if (!user) throw new Error('No user profile found');
    
    if (IS_MOCK_MODE) {
      // Handle mock mode locally
      const updatedProfile: UserProfile = {
        ...user,
        termsAccepted: true,
        termsAcceptedDate: new Date()
      };
      
      const userProfileKey = `firestore-users/${firebaseUser.uid}`;
      localStorage.setItem(userProfileKey, JSON.stringify(updatedProfile));
      setUser(updatedProfile);
    } else {
      try {
        // Use backend API to accept terms
        console.log('📋 Accepting terms via backend API...');
        const updatedBackendUser = await userApi.acceptTermsAndConditions();
        console.log('✅ Terms accepted via backend:', updatedBackendUser);
        
        // Update local profile with backend response
        const updatedProfile: UserProfile = {
          ...user,
          termsAccepted: updatedBackendUser.termsAndConditionsAccepted,
          termsAcceptedDate: updatedBackendUser.termsAcceptedAt ? new Date(updatedBackendUser.termsAcceptedAt) : new Date(),
          backendAccount: updatedBackendUser
        };
        
        setUser(updatedProfile);
      } catch (error) {
        console.error('❌ Failed to accept terms via backend:', error);
        throw error; // Don't continue with fallback, just throw the error
      }
    }
  };

  const logout = async (): Promise<void> => {
    if (!auth) {
      console.warn('Auth not configured');
      return;
    }
    
    // Reset OTP verification flag
    setOtpVerified(false);
    
    if (IS_MOCK_MODE) {
      await (auth as any).signOut();
    } else {
      await signOut(auth);
    }
  };

  const resetPassword = () => {
    // For phone auth, password reset would be through phone verification
    console.log('Phone-based password reset would be implemented here');
    alert('For phone authentication, account recovery is done through phone verification');
  };

  const value: UserContextType = {
    user,
    firebaseUser,
    loading,
    setupRecaptcha,
    sendOTP,
    verifyOTP,
    completeProfile,
    updateProfile,
    acceptTerms,
    syncBackendAccount,
    logout,
    resetPassword
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
